import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';
import { usePlan } from './PlanContext';

const BranchContext = createContext();

/**
 * Hook to access branch state.
 *
 * Returns { branches, selectedBranchId, selectBranch, loading, refetchBranches, isMultiBranch }
 */
export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(
    () => localStorage.getItem('vc-selected-branch') || 'all'
  );
  const [loading, setLoading] = useState(true);
  const { hasPlanFeature } = usePlan();

  const isMultiBranch = hasPlanFeature('multiBranch');

  const fetchBranches = useCallback(async () => {
    try {
      const user = getCurrentUser();
      if (!user || user.usertype === 'tenantadmin' || user.usertype === 'student') {
        setBranches([]);
        setLoading(false);
        return;
      }

      if (!isMultiBranch) {
        setBranches([]);
        setLoading(false);
        return;
      }

      const { data } = await apiClient.get('/branches');
      const fetched = data?.branches || [];
      setBranches(fetched);

      // Auto-select default branch on first load if nothing saved
      const saved = localStorage.getItem('vc-selected-branch');
      if (!saved && fetched.length > 0) {
        const defaultBranch = fetched.find(b => b.isDefault) || fetched[0];
        setSelectedBranchId(defaultBranch._id);
        localStorage.setItem('vc-selected-branch', defaultBranch._id);
      }
    } catch (err) {
      console.warn('Failed to fetch branches:', err.message);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [isMultiBranch]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Listen for auth changes — refetch on login/logout
  useEffect(() => {
    const handleAuth = () => {
      setSelectedBranchId('all');
      localStorage.removeItem('vc-selected-branch');
      fetchBranches();
    };
    window.addEventListener('vc-auth-change', handleAuth);
    return () => window.removeEventListener('vc-auth-change', handleAuth);
  }, [fetchBranches]);

  const selectBranch = useCallback((branchId) => {
    const val = branchId || 'all';
    setSelectedBranchId(val);
    if (val === 'all') {
      localStorage.removeItem('vc-selected-branch');
    } else {
      localStorage.setItem('vc-selected-branch', val);
    }
    // Notify pages to re-fetch with new branch scope
    window.dispatchEvent(new Event('vc-branch-change'));
  }, []);

  return (
    <BranchContext.Provider value={{
      branches,
      selectedBranchId,
      selectBranch,
      loading,
      refetchBranches: fetchBranches,
      isMultiBranch,
    }}>
      {children}
    </BranchContext.Provider>
  );
};
